import { Alert, Empty, List, Modal, Tag, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { api, type EventRow, type SpeakerRow } from '../lib/api';

const { Text } = Typography;

export function AssignSpeakerToEventsModal({
  open,
  speaker,
  onClose,
  onDone,
}: {
  open: boolean;
  speaker: SpeakerRow | null;
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [memberOf, setMemberOf] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !speaker) return;
    setLoading(true);
    void (async () => {
      try {
        const [allEvents, speakerEvents] = await Promise.all([
          api<EventRow[]>('/api/events'),
          api<EventRow[]>(`/api/speakers/${speaker.id}/events`),
        ]);
        setEvents(allEvents);
        setMemberOf(new Set(speakerEvents.map((e) => e.id)));
      } catch (err) {
        message.error((err as { error?: string }).error ?? 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, speaker]);

  const attach = async (eventId: string) => {
    if (!speaker) return;
    setBusyId(eventId);
    try {
      await api(`/api/events/${eventId}/speakers`, {
        method: 'POST',
        body: JSON.stringify({ speaker_id: speaker.id }),
      });
      setMemberOf((prev) => new Set(prev).add(eventId));
      await onDone();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to attach');
    } finally {
      setBusyId(null);
    }
  };

  const detach = async (eventId: string) => {
    if (!speaker) return;
    setBusyId(eventId);
    try {
      // Find the event_speaker row for this speaker on this event and remove it
      const detail = await api<{
        speakers: { id: string; speaker_id: string }[];
      }>(`/api/events/${eventId}`);
      const es = detail.speakers.find((s) => s.speaker_id === speaker.id);
      if (!es) return;
      await api(`/api/events/${eventId}/speakers/${es.id}`, { method: 'DELETE' });
      setMemberOf((prev) => {
        const next = new Set(prev);
        next.delete(eventId);
        return next;
      });
      await onDone();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to detach');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Modal
      title={`Assign ${speaker?.name ?? 'speaker'} to events`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Tap an event to add or remove this speaker. You can attach the same
        speaker to as many events as you like.
      </Text>
      {events.length === 0 && !loading && (
        <Empty description="No events yet — create one from the Events tab." />
      )}
      <List
        loading={loading}
        dataSource={events}
        renderItem={(ev) => {
          const isMember = memberOf.has(ev.id);
          return (
            <List.Item
              actions={[
                <a
                  key="toggle"
                  onClick={() => (isMember ? detach(ev.id) : attach(ev.id))}
                  style={{ opacity: busyId === ev.id ? 0.5 : 1 }}
                >
                  {isMember ? 'Remove' : 'Add'}
                </a>,
              ]}
            >
              <List.Item.Meta
                title={
                  <span>
                    {ev.title}{' '}
                    {isMember && <Tag color="green">On event</Tag>}
                  </span>
                }
                description={
                  ev.date ? dayjs(ev.date).format('dddd, D MMMM YYYY') : 'No date set'
                }
              />
            </List.Item>
          );
        }}
      />
      <Alert
        style={{ marginTop: 16 }}
        type="info"
        showIcon
        message="Slide control, role, and notes are configured per event from each event's page."
      />
    </Modal>
  );
}
